import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { CheckboxButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { TableHeader, TableRow, TableTitle } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/table/Table'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { groupByCategory, sumDuration } from '../data/npTestPlanMeasureUtils'
import { NpTestPlanMeasure } from '../data/npTestPlanTypes'
import { useNpTestPlanMeasureLabels } from '../hooks/useNpTestPlanMeasureLabels'

export interface NpTestPlanLibraryTableProps {
  measures: NpTestPlanMeasure[]
  selectedIds: Set<string>
  planIds: Set<string>
  onToggle: (measure: NpTestPlanMeasure) => void
}

const Header = styled(TableHeader)<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  alignItems: 'center',
  gap: spacing['16px'],
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['24px'],
  backgroundColor: colors.backgroundSection,
}))

const Row = styled(TableRow)<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  alignItems: 'center',
  gap: spacing['16px'],
  paddingVertical: spacing['12px'],
  paddingHorizontal: spacing['24px'],
  borderBottomWidth: 1,
  borderBottomColor: colors.borderDefault,
  borderStyle: 'solid',
}))

const Name = styled.View({
  flex: 1,
})

const Column = styled.View({
  width: '150px',
})

const CategoryBand = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  alignItems: 'center',
  paddingVertical: spacing['8px'],
  paddingHorizontal: spacing['24px'],
  backgroundColor: colors.backgroundSection,
}))

const EmptyState = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignItems: 'center',
  paddingVertical: spacing['48px'],
}))

export const NpTestPlanLibraryTable: FunctionComponent<NpTestPlanLibraryTableProps> = ({
  measures,
  selectedIds,
  planIds,
  onToggle,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  const { formatAgeRange, formatDuration, formatInformant, formatSessionFormat } = useNpTestPlanMeasureLabels()

  const columns: { heading: string; format: (measure: NpTestPlanMeasure) => string }[] = [
    {
      heading: formatMessage({
        defaultMessage: 'FORMAT',
        description: 'Column heading for how a test measure is administered',
      }),
      format: formatSessionFormat,
    },
    {
      heading: formatMessage({
        defaultMessage: 'INFORMANT',
        description: 'Column heading for who answers a test measure',
      }),
      format: (measure) => formatInformant(measure.informant),
    },
    {
      heading: formatMessage({
        defaultMessage: 'AGE RANGE',
        description: 'Column heading for the ages a test measure suits',
      }),
      format: formatAgeRange,
    },
    {
      heading: formatMessage({
        defaultMessage: 'DURATION',
        description: 'Column heading for how long a test measure takes',
      }),
      format: (measure) => formatDuration(sumDuration([measure])),
    },
  ]

  if (!measures.length) {
    return (
      <EmptyState testID={tID('NpTestPlanLibraryTable-empty')}>
        <BodyText
          size={BodyTextSize.DEFAULT}
          color={colors.textSecondary}
          text={formatMessage({
            defaultMessage: 'No measures match your search.',
            description: 'Shown when a search of the neuropsych measure library matches nothing',
          })}
        />
      </EmptyState>
    )
  }

  return (
    <>
      <Header>
        <Name>
          <TableTitle
            text={formatMessage({ defaultMessage: 'NAME', description: 'Column heading for a test measure name' })}
          />
        </Name>
        {columns.map(({ heading }) => (
          <Column key={heading}>
            <TableTitle text={heading} />
          </Column>
        ))}
      </Header>
      {groupByCategory(measures).map(({ category, measures: banded }) => (
        <React.Fragment key={category}>
          <CategoryBand testID={tID(`NpTestPlanLibraryTable-category-${category}`)}>
            <BodyText bold size={BodyTextSize.SMALL} text={category} />
          </CategoryBand>
          {banded.map((measure) => {
            const readOnly = planIds.has(measure.id)
            return (
              <Row key={measure.id} testID={tID(`NpTestPlanLibraryRow-${measure.id}`)}>
                <Name>
                  <Checkbox
                    testID={tID(`NpTestPlanLibraryRow-${measure.id}-checkbox`)}
                    name={`NpTestPlanLibraryRow-${measure.id}`}
                    buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
                    title={measure.name}
                    titleColor={readOnly ? colors.textInactive : colors.textPrimary}
                    checked={selectedIds.has(measure.id)}
                    readOnly={readOnly}
                    onPress={() => onToggle(measure)}
                    hideBaseInput
                    accessibilityLabel={
                      readOnly
                        ? formatMessage(
                            {
                              defaultMessage: '{measureName}, already on the test plan',
                              description:
                                'Announced for a test measure the plan already includes, which cannot be unticked',
                            },
                            { measureName: measure.name },
                          )
                        : measure.name
                    }
                  />
                </Name>
                {columns.map(({ heading, format }) => (
                  <Column key={heading}>
                    <BodyText
                      size={BodyTextSize.SMALL}
                      color={readOnly ? colors.textInactive : colors.textSecondary}
                      text={format(measure)}
                    />
                  </Column>
                ))}
              </Row>
            )
          })}
        </React.Fragment>
      ))}
    </>
  )
}
